import { UserType } from 'dy-frontend-http-repository/lib/data/enums';
import { SubscriptionMode } from 'dy-frontend-http-repository/lib/modules/Subscription/enums';
import {
    FlexMonthLastUsedResource,
    FlexMonthStatusResource,
    SubscriptionItemResource,
    SubscriptionResource,
} from 'dy-frontend-http-repository/lib/modules/Subscription/resources';
import { SubscriptionPermission } from 'dy-frontend-permissions/lib/permission';
import { CouponUtils, PriceUtils } from 'dy-frontend-shared/lib/utils';
import { useStore } from 'effector-react';
import moment from 'moment';
import React, { HTMLAttributes, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Pagination from '@app/components/Pagination';
import CardPreview from '@app/containers/components/CardPreview';
import CreateSubscriptionModal, { CreateSubscriptionModalProps } from '@app/containers/modals/CreateSubscriptionModal';
import { $permissions } from '@app/containers/store/states';
import { Alert, Button, Card, Colors, Divider, Icon, Intent, Spinner, Tag } from '@blueprintjs/core';
import Circle from '@components/Circle';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import ListCollapse from '@components/ListCollapse';
import NonIdealState from '@components/NonIdealState';
import DevText from '@components/Text';
import SubscriptionStateTags from '@containers/components/SubscriptionStateTags';
import { Endpoints } from '@data/consts';
import { openModal } from '@modals/store/events';
import { $client, $clientPaymentAccount } from '@pages/Client/store/states';

import { amountOfClientInvoicesOnPage, amountOfSubscriptionsOnPage } from '../../consts';
import CancelFlexMonthModal, { CancelFlexMonthModalProps } from '../../modals/CancelFlexMonthModal';
import ScheduleFlexMonthModal, { ScheduleFlexMonthModalProps } from '../../modals/ScheduleFlexMonthModal';
import { fetchFlexMonthStatus, fetchSubscriptions } from '../../store/effects';
import { $flexMonthStatuses, $subscriptions } from '../../store/states';

// Human-readable explanations for backend Flex Month "blocked" ineligibility reasons
const flexMonthBlockedReasonInformation: { [reason: string]: string } = {
    already_scheduled: 'A Flex Month is already scheduled for this subscription',
    subscription_terminated: 'Subscription is terminated',
    subscription_state_conflict: 'Subscription state does not allow a Flex Month',
    manual_pause_conflict: 'A manual pause or pause schedule is already in place',
    cancel_at_period_end: 'Subscription is set to cancel at period end',
    pending_schedule: 'Subscription has a pending update schedule',
    renewal_too_close: 'The next renewal is too close to schedule a Flex Month',
};

export type Props = HTMLAttributes<HTMLDivElement>;

const Subscriptions: React.FC<Props> = (props) => {
    const permissions = useStore($permissions);
    const navigate = useNavigate();

    const client = useStore($client);
    const clientSubscriptions = useStore($subscriptions);
    const clientPaymentAccount = useStore($clientPaymentAccount);
    const flexMonthStatuses = useStore($flexMonthStatuses);
    const isFetchingClientSubscriptions = useStore(fetchSubscriptions.pending);

    const [isSubscriptionsCollapsed, setIsSubscriptionsCollapsed] = useState(true);
    const [page, setPage] = useState(1);
    const [flexMonthAlreadyUsed, setFlexMonthAlreadyUsed] = useState<FlexMonthLastUsedResource | null>(null);

    const isSubscriptionRootPermission = permissions.has(SubscriptionPermission.ROOT);

    const handleLoadClientSubscriptionsPage = async (newPage: number) => {
        if (!clientPaymentAccount) return;

        const pageOffset = (newPage - 1) * amountOfClientInvoicesOnPage;

        try {
            await fetchSubscriptions({
                pagination: { _pagination: { limit: amountOfSubscriptionsOnPage, offset: pageOffset } },
                filter: { payment_account_id: clientPaymentAccount.id, is_ended: false },
            });
        } catch (e) {
            // TODO: handle error
            console.error(e);
        }
    };

    useEffect(() => {
        handleLoadClientSubscriptionsPage(page);

        // eslint-disable-next-line
    }, [page]);

    useEffect(() => {
        if (!clientSubscriptions) {
            return;
        }

        // Fetch Flex Month state for every subscription card (statuses are stored per subscription ID)
        clientSubscriptions.items.forEach((subscription) => {
            fetchFlexMonthStatus(subscription.id).catch((e) => {
                // TODO: handle error
                console.error(e);
            });
        });

        // eslint-disable-next-line
    }, [clientSubscriptions]);

    if (!client || !clientPaymentAccount) {
        return null;
    }

    const renderSubscriptionHeader = (subscription: SubscriptionResource) => {
        return (
            <Flex align="center" justify="space-between">
                <Flex direction="row" align="center">
                    <SubscriptionStateTags subscription={subscription} />
                    <Heading type="h5">{moment(subscription.current_period.start_at).format('D MMM, YYYY')}</Heading>
                    <Icon className="ml-1 mr-1" icon="chevron-right" color={Colors.GRAY2} />
                    <Heading type="h5">{moment(subscription.current_period.end_at).format('D MMM, YYYY')}</Heading>
                </Flex>

                <Button onClick={() => navigate(Endpoints.SUBSCRIPTION.replace(':subscriptionId', subscription.id))}>
                    Go to Subscription page
                </Button>
            </Flex>
        );
    };

    const renderSubscriptionItem = (subscriptionItem: SubscriptionItemResource) => {
        const renderUnknownSubscriptionItem = () => {
            return (
                <Flex className="mt-1" direction="row" align="center">
                    <Icon icon="help" className="mr-1" color={Colors.GRAY2} />
                    <Heading muted type="h5" className="mr-1">
                        Unknown additional payment
                    </Heading>
                    <DevText muted>x{subscriptionItem.quantity}</DevText>
                    <Circle className="ml-2 mr-2" size="6px" color={Colors.GRAY2} />
                    <Icon className="mr-1" icon="warning-sign" color={Colors.ORANGE4} />
                    <DevText muted>Go to subscription page and fetch price data</DevText>
                </Flex>
            );
        };

        const price = subscriptionItem.price;

        if (!price) {
            return renderUnknownSubscriptionItem();
        }

        // Get currency
        const subscriptionItemPriceCurrency = price.currency.toUpperCase();

        // Get price for single subscription item
        const subscriptionItemPriceFormatted = PriceUtils.formatPrice({
            price: price.unit_amount,
            shouldDisplayCents: true,
            currency: price.currency,
        });

        // Get label for recurring interval "2 days", "day", "3 months", etc.
        const subscriptionItemRecurringInterval = PriceUtils.getRecurringInterval(
            price.recurring_interval,
            price.recurring_interval_count
        );

        const renderSubscriptionItemWithPlan = () => {
            const plan = price.plan!;

            return (
                <Flex className="mt-1" direction="row" align="center">
                    <Icon icon="box" className="mr-1" color={plan.color} />
                    <Heading type="h5" className="mr-1">
                        {plan.title}
                    </Heading>
                    <DevText muted>x{subscriptionItem.quantity}</DevText>
                    <Circle className="ml-2 mr-2" size="6px" color={Colors.GRAY2} />
                    <DevText muted>
                        {subscriptionItemPriceFormatted} {subscriptionItemPriceCurrency} /{' '}
                        {subscriptionItemRecurringInterval}
                    </DevText>
                </Flex>
            );
        };

        const renderSubscriptionItemWithoutPlan = () => {
            return (
                <Flex className="mt-1" direction="row" align="center">
                    <Icon icon="bank-account" className="mr-1" />
                    <Heading type="h5" className="mr-1">
                        Additional payment: {subscriptionItemPriceFormatted}
                    </Heading>
                    <DevText muted>x{subscriptionItem.quantity}</DevText>
                    <Circle className="ml-2 mr-2" size="6px" color={Colors.GRAY2} />
                    <DevText muted>
                        {subscriptionItemPriceFormatted} {subscriptionItemPriceCurrency} /{' '}
                        {subscriptionItemRecurringInterval}
                    </DevText>
                </Flex>
            );
        };

        if (price.plan) {
            // Plan related to price
            return renderSubscriptionItemWithPlan();
        } else {
            // Plan is NOT related to price, meaning it's regular payment item
            return renderSubscriptionItemWithoutPlan();
        }
    };

    const renderSubscriptionItems = (subscription: SubscriptionResource) => {
        if (subscription.items.length === 0) {
            // Subscription do NOT have any subscription items
            return null;
        }

        return (
            <>
                <Divider className="mt-1 mb-1" />
                <div>
                    <DevText muted className="mb-small">
                        Subscription items
                    </DevText>

                    {subscription.items.map(renderSubscriptionItem)}
                </div>
            </>
        );
    };

    const renderSubscriptionDiscounts = (subscription: SubscriptionResource) => {
        const discounts = subscription.discounts;
        if (discounts.length === 0) {
            // Not discounts
            return null;
        }

        return (
            <div className="mt-2">
                <DevText muted className="mb-small">
                    {discounts.length > 1 ? 'Discounts' : 'Discount'}
                </DevText>

                {discounts.map((discount, index) => {
                    const coupon = discount.coupon;
                    if (!coupon) {
                        // Subscription do NOT have coupon attached
                        return (
                            <Flex className={index === 0 ? '' : 'mt-small'} key={discount.external_id}>
                                <Icon className="mr-1" icon="warning-sign" color={Colors.ORANGE4} />
                                <DevText muted>Coupon details are unknown, sync it to get info</DevText>
                            </Flex>
                        );
                    }

                    // Get duration text
                    const durationText = CouponUtils.getCouponDurationText(coupon);

                    // Get price/percentage discount text
                    const couponDiscountText = CouponUtils.getCouponDiscountText(coupon);

                    return (
                        <div className={index === 0 ? '' : 'mt-small'} key={coupon.id}>
                            <DevText>
                                {couponDiscountText} / {coupon.title} / {durationText}
                            </DevText>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderSubscriptionPaymentMethod = (subscription: SubscriptionResource) => {
        const renderSubscriptionPaymentMethodLabel = () => {
            const paymentMethod = subscription.payment_method;

            if (!paymentMethod) {
                return <DevText>Default or current payment method used</DevText>;
            }

            return (
                <Flex direction="row" align="center">
                    <CardPreview className="mr-1" width="40px" height="30px" type={paymentMethod.card_brand} />
                    <DevText>
                        {paymentMethod.card_brand.toUpperCase()} ending in *{paymentMethod.card_last_four}
                    </DevText>
                    <DevText muted className="ml-1">
                        {`${paymentMethod.card_expiration_month}`.padStart(2, '0')}/{paymentMethod.card_expiration_year}
                    </DevText>
                </Flex>
            );
        };

        return (
            <div className="mt-2">
                <DevText muted className="mb-small">
                    Payment Method
                </DevText>

                {renderSubscriptionPaymentMethodLabel()}
            </div>
        );
    };

    const renderSubscriptionTrial = (subscription: SubscriptionResource) => {
        if (!subscription.trial.end_at) {
            // Subscription do NOT have trial end date set
            return null;
        }

        return (
            <div className="mt-2">
                <DevText muted className="mb-small">
                    Trial
                </DevText>

                <DevText>Set trial end {moment(subscription.trial.end_at).fromNow()}</DevText>
            </div>
        );
    };

    const renderCreateSubscriptionButton = () => {
        const isAllowed = isSubscriptionRootPermission || permissions.has(SubscriptionPermission.CREATE);
        if (!isAllowed) {
            return null;
        }

        return (
            <Button
                intent={Intent.PRIMARY}
                onClick={() =>
                    openModal<CreateSubscriptionModalProps>({
                        ModalComponent: CreateSubscriptionModal,
                        data: {
                            userId: client.id,
                            userType: UserType.CLIENT,
                            subscriptionMode: SubscriptionMode.USER,
                            refetchOnSuccess: () =>
                                page === 1 ? handleLoadClientSubscriptionsPage(page) : () => setPage(1),
                        },
                    })
                }
            >
                Create subscription
            </Button>
        );
    };

    const renderSubscriptionFlexMonth = (subscription: SubscriptionResource) => {
        const flexMonthStatus: FlexMonthStatusResource | undefined = flexMonthStatuses[subscription.id];

        if (!flexMonthStatus) {
            // Flex Month status for this subscription was NOT fetched yet
            return null;
        }

        const isPauseApplyAllowed = isSubscriptionRootPermission || permissions.has(SubscriptionPermission.PAUSE_APPLY);
        const isPauseReleaseAllowed =
            isSubscriptionRootPermission || permissions.has(SubscriptionPermission.PAUSE_RELEASE);

        const refetchFlexMonthStatus = () => fetchFlexMonthStatus(subscription.id);

        const renderFlexMonthState = () => {
            const flexMonth = flexMonthStatus.flex_month;

            switch (flexMonthStatus.ui_state) {
                case 'schedule_available':
                    if (!isPauseApplyAllowed) {
                        return <DevText muted>Available, but you are not allowed to pause subscriptions</DevText>;
                    }

                    return (
                        <Button
                            onClick={() =>
                                openModal<ScheduleFlexMonthModalProps>({
                                    ModalComponent: ScheduleFlexMonthModal,
                                    data: {
                                        subscriptionId: subscription.id,
                                        nextBillingAt: subscription.current_period.end_at,
                                        refetchOnSuccess: refetchFlexMonthStatus,
                                    },
                                })
                            }
                        >
                            Pause Subscription
                        </Button>
                    );

                case 'already_used':
                    if (!isPauseApplyAllowed) {
                        return <DevText muted>You are not allowed to pause subscriptions</DevText>;
                    }

                    return (
                        <Button onClick={() => setFlexMonthAlreadyUsed(flexMonthStatus.last_used)}>
                            Pause Subscription
                        </Button>
                    );

                case 'cancellable':
                    if (!flexMonth) {
                        return null;
                    }

                    return (
                        <Flex direction="row" align="center">
                            <Tag className="mr-1" intent={Intent.PRIMARY} minimal>
                                Flex Month scheduled
                            </Tag>
                            <DevText muted className="mr-1">
                                Pause starts {moment(flexMonth.pause_start_date_pacific).format('D MMM, YYYY')}, billing
                                resumes {moment(flexMonth.resume_date_pacific).format('D MMM, YYYY')}
                            </DevText>
                            {isPauseReleaseAllowed && (
                                <Button
                                    minimal
                                    intent={Intent.DANGER}
                                    onClick={() =>
                                        openModal<CancelFlexMonthModalProps>({
                                            ModalComponent: CancelFlexMonthModal,
                                            data: {
                                                subscriptionId: subscription.id,
                                                flexMonth: flexMonth,
                                                refetchOnSuccess: refetchFlexMonthStatus,
                                            },
                                        })
                                    }
                                >
                                    Cancel Flex Month
                                </Button>
                            )}
                        </Flex>
                    );

                case 'cutoff_passed':
                    if (!flexMonth) {
                        return null;
                    }

                    return (
                        <Flex direction="row" align="center">
                            <Tag className="mr-1" intent={Intent.PRIMARY} minimal>
                                Flex Month scheduled
                            </Tag>
                            <DevText muted className="mr-1">
                                Pause starts {moment(flexMonth.pause_start_date_pacific).format('D MMM, YYYY')}, billing
                                resumes {moment(flexMonth.resume_date_pacific).format('D MMM, YYYY')}
                            </DevText>
                            <Button minimal disabled>
                                Cancellation window (48h) has passed
                            </Button>
                        </Flex>
                    );

                case 'active':
                    if (!flexMonth) {
                        return null;
                    }

                    return (
                        <Flex direction="row" align="center">
                            <Tag className="mr-1" intent={Intent.SUCCESS} minimal>
                                Flex Month active
                            </Tag>
                            <DevText muted>
                                Billing resumes {moment(flexMonth.resume_date_pacific).format('D MMM, YYYY')}
                            </DevText>
                        </Flex>
                    );

                case 'resume_failed':
                    if (!flexMonth) {
                        return null;
                    }

                    return (
                        <Flex direction="row" align="center">
                            <Tag className="mr-1" intent={Intent.DANGER} minimal>
                                Billing resume needs attention
                            </Tag>
                            <DevText muted>
                                The Flex Month remains active while automatic resume retries continue. Planned resume
                                date: {moment(flexMonth.resume_date_pacific).format('D MMM, YYYY')}.
                            </DevText>
                        </Flex>
                    );

                case 'plan_not_eligible':
                    return (
                        <Flex direction="row" align="center">
                            <Button className="mr-1" disabled>
                                Pause Subscription
                            </Button>
                            <DevText muted>Flex Month is available only for Summit and Apex</DevText>
                        </Flex>
                    );

                case 'blocked':
                default:
                    return (
                        <Flex direction="row" align="center">
                            <Button className="mr-1" disabled>
                                Pause Subscription
                            </Button>
                            <DevText muted>
                                {(flexMonthStatus.ineligibility_reason &&
                                    flexMonthBlockedReasonInformation[flexMonthStatus.ineligibility_reason]) ??
                                    'Flex Month cannot be scheduled right now'}
                            </DevText>
                        </Flex>
                    );
            }
        };

        const flexMonthLabel =
            flexMonthStatus.ui_state === 'schedule_available'
                ? 'Flex Month eligible'
                : flexMonthStatus.ui_state === 'already_used'
                  ? 'Flex Month ineligible'
                  : 'Flex Month';

        return (
            <div className="mt-2">
                <DevText muted className="mb-small">
                    {flexMonthLabel}
                </DevText>

                {renderFlexMonthState()}
            </div>
        );
    };

    const renderSubscription = (subscription: SubscriptionResource) => {
        return (
            <Card compact className="mt-2" key={subscription.id}>
                {renderSubscriptionHeader(subscription)}
                {renderSubscriptionItems(subscription)}
                {renderSubscriptionDiscounts(subscription)}
                {renderSubscriptionPaymentMethod(subscription)}
                {renderSubscriptionTrial(subscription)}
                {renderSubscriptionFlexMonth(subscription)}
            </Card>
        );
    };

    const renderSubscriptions = () => {
        // Client subscriptions were NOT fetched yet
        if (!clientSubscriptions) {
            return (
                <Flex justify="center">
                    <Spinner />
                </Flex>
            );
        }

        if (clientSubscriptions.items.length === 0) {
            return (
                <NonIdealState
                    className="mt-2"
                    icon={<Icon className="mb-2" icon="search" size={70} />}
                    title={
                        <Heading className="mb-1" type="h4">
                            No subscriptions were found
                        </Heading>
                    }
                />
            );
        }

        const renderCollapseControl = (hiddenItemsAmount: number) => {
            let collapseButtonText = `+ ${hiddenItemsAmount} subscriptions more, click to show`;

            if (!isSubscriptionsCollapsed) {
                // Not collapsed
                collapseButtonText = `Hide subscription`;
            }

            return (
                <Button minimal onClick={() => setIsSubscriptionsCollapsed((prev) => !prev)}>
                    {collapseButtonText}
                </Button>
            );
        };

        return (
            <ListCollapse<SubscriptionResource>
                isCollapsed={isSubscriptionsCollapsed}
                maxSize={3}
                list={clientSubscriptions.items}
                itemRenderer={renderSubscription}
                renderCollapseControl={renderCollapseControl}
            />
        );
    };

    const renderPagination = () => {
        // Client brand profiles is not fetched yet
        if (clientSubscriptions === null) {
            return null;
        }

        // Check if offset paginator exist
        if (clientSubscriptions.paginator === null) {
            return;
        }

        // Only one page exist
        if (!clientSubscriptions.paginator.has_more && clientSubscriptions.paginator.offset === 0) {
            return null;
        }

        return (
            <Flex className="mt-2" justify="flex-end">
                <Pagination
                    fetching={isFetchingClientSubscriptions}
                    hasMore={clientSubscriptions.paginator.has_more}
                    page={page}
                    amountOfItemsOnPage={amountOfClientInvoicesOnPage}
                    totalItems={clientSubscriptions.paginator.total}
                    onPageChange={(newPage) => setPage(newPage)}
                />
            </Flex>
        );
    };

    return (
        <div {...props}>
            <Flex justify="space-between" align="center">
                <Heading type="h3">Subscriptions settings</Heading>
                {renderCreateSubscriptionButton()}
            </Flex>

            {renderSubscriptions()}
            {renderPagination()}

            <Alert
                isOpen={flexMonthAlreadyUsed !== null}
                confirmButtonText="Got it"
                intent={Intent.WARNING}
                icon="warning-sign"
                onClose={() => setFlexMonthAlreadyUsed(null)}
            >
                {flexMonthAlreadyUsed !== null && (
                    <DevText>
                        Sorry, this client has already used their Flex Month on {flexMonthAlreadyUsed.month_name},{' '}
                        {flexMonthAlreadyUsed.year}. A Flex month can only be applied once per the last 12 months.
                    </DevText>
                )}
            </Alert>
        </div>
    );
};

export default Subscriptions;
